import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import AppButton from "../../../ui/AppButton";
import AppInputField from "../../../ui/AppInputField";
import { Spinner } from "../../../ui/common";

const contactSchema = z.object({
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  companyName: z.string().optional(),
  email: z.string().email("Please enter a valid email address"),
  phoneNumber: z.string().min(1, "Phone number is required"),
  message: z.string().min(10, "Message must be at least 10 characters long"),
});

type ContactFormData = z.infer<typeof contactSchema>;

const ContactForm = () => {
  const [isLoading, setIsLoading] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<ContactFormData>({
    resolver: zodResolver(contactSchema),
    mode: "onBlur",
    defaultValues: {
      firstName: "",
      lastName: "",
      companyName: "",
      email: "",
      phoneNumber: "",
      message: "",
    },
  });

  const onSubmit = async (data: ContactFormData) => {
    try {
      setIsLoading(true);

      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 2000));

      console.log("Contact form submitted successfully!");
      reset();
    } catch (error) {
      console.error("Submission error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen">
      <div className="md:max-w-[85vw] max-w-full mx-auto flex flex-col lg:flex-row items-center justify-between md:py-20 py-6">
        {/* Left Column - Contact Information */}
        <div className="order-2 md:order-1 lg:w-2/5 bg-gradient-to-b from-primary to-[#F3F3F3] text-white p-8 lg:p-12 h-[664px] rounded-[10px]">
          <div className="max-w-md mx-auto lg:mx-0">
            <h1 className="text-2xl font-bold mb-6">Get in touch</h1>

            <div className="space-y-8">
              <div>
                <h2 className="text-lg font-semibold mb-2 text-white/90">
                  Visit us
                </h2>
                <p className="text-white/80 leading-relaxed">
                  60B Adetokunbo street, off Balogun Rd,
                  <br />
                  Onipanu, Lagos
                </p>
              </div>

              <div>
                <h2 className="text-lg font-semibold mb-2 text-white/90">
                  Chat with us
                </h2>
                <p className="text-white/80 mb-2">
                  Our friendly team is here to help
                </p>
                <p className="text-white font-medium">hello@fleetpronig.com</p>
              </div>

              <div>
                <h2 className="text-lg font-semibold mb-2 text-white/90">
                  Call us
                </h2>
                <p className="text-white/80 mb-2">Mon - Fri from 9am to 5pm</p>
                <p className="text-white font-medium">+23480 5983 4534</p>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - Contact Form */}
        <div className="order-1 md:order-2 lg:w-3/5 w-full p-8 lg:p-12 flex items-center justify-center">
          <div className="w-full max-w-2xl">
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              {/* First Name and Last Name Row */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Controller
                  control={control}
                  name="firstName"
                  render={({ field }) => (
                    <AppInputField
                      label="First name"
                      type="text"
                      error={errors.firstName?.message}
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      name={field.name}
                      className="w-full h-[46px]"
                    />
                  )}
                />

                <Controller
                  control={control}
                  name="lastName"
                  render={({ field }) => (
                    <AppInputField
                      label="Last name"
                      type="text"
                      error={errors.lastName?.message}
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      name={field.name}
                      className="w-full h-[46px]"
                    />
                  )}
                />
              </div>

              {/* Company Name */}
              <Controller
                control={control}
                name="companyName"
                render={({ field }) => (
                  <AppInputField
                    label="Company name"
                    type="text"
                    error={errors.companyName?.message}
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    name={field.name}
                    className="w-full h-[46px]"
                  />
                )}
              />

              {/* Email Address */}
              <Controller
                control={control}
                name="email"
                render={({ field }) => (
                  <AppInputField
                    label="Email address"
                    type="email"
                    error={errors.email?.message}
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    name={field.name}
                    className="w-full h-[46px]"
                  />
                )}
              />

              {/* Phone Number */}
              <Controller
                control={control}
                name="phoneNumber"
                render={({ field }) => (
                  <AppInputField
                    label="Phone number"
                    type="tel"
                    error={errors.phoneNumber?.message}
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    name={field.name}
                    className="w-full h-[46px]"
                  />
                )}
              />

              {/* Message */}
              <div className="space-y-2">
                <label className="block text-gray-700 font-medium text-sm">
                  Message
                </label>
                <Controller
                  control={control}
                  name="message"
                  render={({ field }) => (
                    <textarea
                      {...field}
                      rows={4}
                      placeholder="Leave us a message..."
                      className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all resize-none ${
                        errors.message ? "border-red-500" : "border-gray-300"
                      }`}
                    />
                  )}
                />
                {errors.message && (
                  <p className="text-red-500 text-sm">
                    {errors.message.message}
                  </p>
                )}
              </div>

              {/* Submit Button */}
              <div className="pt-4">
                <AppButton
                  type="submit"
                  disabled={isLoading}
                  className="w-full h-14 text-lg font-medium rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  {isLoading ? <Spinner /> : "Send message"}
                </AppButton>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactForm;
